import {
	FeedbackAnswerState,
	FeedbackAnswerStateResource,
	FeedbackAnswerUpdateRequest,
	FeedbackQuestionType,
	FileStorageResource,
} from '../../../shared/types/generated';
import {
	FeedbackStructureQuestionFormQuestion,
	FeedbackStructureQuestionOption,
} from '../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {
	feedbackAnswerControlName,
	feedbackCommentControlName,
	feedbackOptionCommentControlName,
	feedbackOptionControlName,
} from '../helpers/feedback-structure-preview-control-names';

export type FeedbackAttemptControlValueGetter = (controlName: string) => unknown;

export function createAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): FeedbackAnswerUpdateRequest {
	const comment = questionCommentValue(question, controlNamePrefix, getControlValue);

	switch (question.type) {
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
			return createSelectionAnswerRequest(question, controlNamePrefix, getControlValue, comment);
		case FeedbackQuestionType.MULTI_SELECTION:
			return createMultiSelectionAnswerRequest(question, controlNamePrefix, getControlValue, comment);
		case FeedbackQuestionType.ORDERING:
			return createOrderingAnswerRequest(question, controlNamePrefix, getControlValue, comment);
		case FeedbackQuestionType.FILES:
			return createFilesAnswerRequest(question, controlNamePrefix, getControlValue, comment);
		default:
			return createValueAnswerRequest(question, controlNamePrefix, getControlValue, comment);
	}
}

export function createOneByOneProgressionAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	questionCanBeAutosaved: boolean
): FeedbackAnswerUpdateRequest {
	return questionCanBeAutosaved
		? createAnswerRequest(question, controlNamePrefix, getControlValue)
		: createLifecycleStateRequest(FeedbackAnswerState.OMITTED);
}

export function createTimeoutAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	hasQuestionAnswer: boolean
): FeedbackAnswerUpdateRequest {
	if (hasQuestionAnswer) {
		return {
			...createAnswerRequest(question, controlNamePrefix, getControlValue),
			state: FeedbackAnswerState.ANSWERED,
		};
	}

	return createLifecycleStateRequest(FeedbackAnswerState.TIMED_OUT);
}

export function createLifecycleStateRequest(state: FeedbackAnswerState): FeedbackAnswerUpdateRequest {
	return {
		state,
		value: null,
		comment: null,
		selections: [],
	};
}

export function questionAnswerControlValue(
	question: FeedbackStructureQuestionFormQuestion,
	answer: FeedbackAnswerStateResource | undefined
): unknown {
	switch (question.type) {
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
			return selectedOptionPosition(question, answer);
		case FeedbackQuestionType.SLIDER:
			return scalarAnswerValue(answer) ?? question.configuration.start_value;
		case FeedbackQuestionType.RATING:
			return scalarAnswerValue(answer) ?? 0;
		case FeedbackQuestionType.TEXT_INPUT:
			return scalarAnswerValue(answer);
		case FeedbackQuestionType.DATE_TIME:
			return dateTimeAnswerValue(answer);
		case FeedbackQuestionType.TIME:
			return timeAnswerValue(answer);
		case FeedbackQuestionType.DATE:
			return dateAnswerValue(answer);
		case FeedbackQuestionType.FILES:
			return answer?.files ?? [];
		default:
			return null;
	}
}

export function normalizeTextValue(value: unknown): string | null {
	if (value === null || value === undefined) {
		return null;
	}

	const normalizedValue = String(value).trim();
	return normalizedValue === '' ? null : normalizedValue;
}

export function normalizedAnswerValue(question: FeedbackStructureQuestionFormQuestion, value: unknown): string | null {
	switch (question.type) {
		case FeedbackQuestionType.DATE:
			return serializeDateValue(value);
		case FeedbackQuestionType.TIME:
			return serializeTimeValue(value);
		case FeedbackQuestionType.DATE_TIME:
			return serializeDateTimeValue(value);
		default:
			return normalizeTextValue(value);
	}
}

export function questionCommentValue(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): string | null {
	if (!hasQuestionCommentConfiguration(question) || question.configuration.commentable !== true) {
		return null;
	}

	return normalizeTextValue(getControlValue(feedbackCommentControlName(question, controlNamePrefix)));
}

export function optionCommentValueFromForm(
	question: FeedbackStructureQuestionFormQuestion,
	option: FeedbackStructureQuestionOption,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): string | null {
	if (option.commentable !== true) {
		return null;
	}

	return normalizeTextValue(getControlValue(feedbackOptionCommentControlName(question, option, controlNamePrefix)));
}

export function selectedFiles(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): FileStorageResource[] {
	const value = getControlValue(feedbackAnswerControlName(question, controlNamePrefix));
	return Array.isArray(value) ? value.filter((file): file is FileStorageResource => isFileStorageResource(file)) : [];
}

export function scalarAnswerValue(answer: FeedbackAnswerStateResource | undefined): unknown {
	const value = answer?.value;
	if (Array.isArray(value)) {
		return value.length === 0 ? null : value[0];
	}

	if (value === null || value === undefined) {
		return null;
	}

	return value;
}

export function selectedOptionPosition(
	question: FeedbackStructureQuestionFormQuestion,
	answer: FeedbackAnswerStateResource | undefined
): number | null {
	const optionId = answer?.selections[0]?.option_id;
	const option = question.options.find((questionOption) => questionOption.id === optionId);

	return option?.position ?? null;
}

export function isOptionSelected(
	answer: FeedbackAnswerStateResource | undefined,
	option: FeedbackStructureQuestionOption
): boolean {
	return option.id !== undefined && (answer?.selections ?? []).some((selection) => selection.option_id === option.id);
}

export function optionCommentValue(
	answer: FeedbackAnswerStateResource | undefined,
	option: FeedbackStructureQuestionOption
): string {
	if (option.id === undefined) {
		return '';
	}

	return answer?.selections.find((selection) => selection.option_id === option.id)?.comment ?? '';
}

export function orderingPosition(
	answer: FeedbackAnswerStateResource | undefined,
	option: FeedbackStructureQuestionOption
): number {
	if (option.id === undefined) {
		return option.position + 1;
	}

	const selection = (answer?.selections ?? []).find((currentSelection) => currentSelection.option_id === option.id);
	const selectionWithPosition = selection as (typeof selection & {position?: number | string}) | undefined;
	const selectionPosition = 'position' in (selectionWithPosition ?? {}) ? Number(selectionWithPosition?.position) : Number.NaN;
	if (Number.isFinite(selectionPosition)) {
		return selectionPosition + 1;
	}

	const selectionIndex = (answer?.selections ?? []).findIndex((currentSelection) => currentSelection.option_id === option.id);
	return selectionIndex >= 0 ? selectionIndex + 1 : option.position + 1;
}

export function dateAnswerValue(answer: FeedbackAnswerStateResource | undefined): Date | null {
	return parseDateControlValue(scalarAnswerValue(answer));
}

export function timeAnswerValue(answer: FeedbackAnswerStateResource | undefined): Date | null {
	return parseTimeControlValue(scalarAnswerValue(answer));
}

export function dateTimeAnswerValue(answer: FeedbackAnswerStateResource | undefined): Date | null {
	return parseDateTimeControlValue(scalarAnswerValue(answer));
}

export function parseDateControlValue(value: unknown): Date | null {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	const normalizedValue = normalizeTextValue(value);
	if (normalizedValue === null) {
		return null;
	}

	const parsedDate = new Date(normalizedValue);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function parseTimeControlValue(value: unknown): Date | null {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	const normalizedValue = normalizeTextValue(value);
	if (normalizedValue === null) {
		return null;
	}

	const timeMatch = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/.exec(normalizedValue);
	if (timeMatch === null) {
		return null;
	}

	const parsedDate = new Date();
	parsedDate.setHours(Number(timeMatch[1]), Number(timeMatch[2]), Number(timeMatch[3] ?? '0'), 0);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function parseDateTimeControlValue(value: unknown): Date | null {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	const normalizedValue = normalizeTextValue(value);
	if (normalizedValue === null) {
		return null;
	}

	const parsedDate = new Date(normalizedValue);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function optionOrder(
	question: FeedbackStructureQuestionFormQuestion,
	option: FeedbackStructureQuestionOption,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter
): number {
	const value = Number(getControlValue(feedbackOptionControlName(question, option, controlNamePrefix)));
	return Number.isFinite(value) ? value : option.position + 1;
}

export function hasQuestionCommentConfiguration(
	question: FeedbackStructureQuestionFormQuestion
): question is FeedbackStructureQuestionFormQuestion & {configuration: {commentable: boolean; comment_prompt: string | null}} {
	return 'commentable' in question.configuration;
}

export function isFileStorageResource(value: unknown): value is FileStorageResource {
	return typeof value === 'object'
		&& value !== null
		&& 'uuid' in value
		&& typeof value.uuid === 'string';
}

function createSelectionAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	comment: string | null
): FeedbackAnswerUpdateRequest {
	const selectedPosition = getControlValue(feedbackAnswerControlName(question, controlNamePrefix));
	const selectedOptionPosition = typeof selectedPosition === 'number'
		? selectedPosition
		: typeof selectedPosition === 'string' && selectedPosition.trim() !== ''
			? Number(selectedPosition)
			: null;
	const selectedOption = selectedOptionPosition === null || Number.isNaN(selectedOptionPosition)
		? undefined
		: question.options.find((option) => option.position === selectedOptionPosition && option.id !== undefined);
	const selections = selectedOption === undefined
		? []
		: [{
			option_id: selectedOption.id!,
			comment: optionCommentValueFromForm(question, selectedOption, controlNamePrefix, getControlValue),
		}];

	return createAnswerRequestBody(selections.length > 0, null, comment, selections);
}

function createMultiSelectionAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	comment: string | null
): FeedbackAnswerUpdateRequest {
	const selections = question.options
		.filter((option) => option.id !== undefined && getControlValue(feedbackOptionControlName(question, option, controlNamePrefix)) === true)
		.map((option) => ({
			option_id: option.id!,
			comment: optionCommentValueFromForm(question, option, controlNamePrefix, getControlValue),
		}));

	return createAnswerRequestBody(selections.length > 0, null, comment, selections);
}

function createOrderingAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	comment: string | null
): FeedbackAnswerUpdateRequest {
	const selections = [...question.options]
		.filter((option): option is FeedbackStructureQuestionOption & {id: number} => option.id !== undefined)
		.sort((first, second) => optionOrder(question, first, controlNamePrefix, getControlValue) - optionOrder(question, second, controlNamePrefix, getControlValue))
		.map((option) => ({
			option_id: option.id,
			comment: optionCommentValueFromForm(question, option, controlNamePrefix, getControlValue),
		}));

	return createAnswerRequestBody(selections.length > 0, null, comment, selections);
}

function createValueAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	comment: string | null
): FeedbackAnswerUpdateRequest {
	const value = getControlValue(feedbackAnswerControlName(question, controlNamePrefix));
	const normalizedValue = normalizedAnswerValue(question, value);

	return createAnswerRequestBody(normalizedValue !== null, normalizedValue, comment, []);
}

function createFilesAnswerRequest(
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	getControlValue: FeedbackAttemptControlValueGetter,
	comment: string | null
): FeedbackAnswerUpdateRequest {
	const files = selectedFiles(question, controlNamePrefix, getControlValue);

	return createAnswerRequestBody(files.length > 0, null, comment, []);
}

function createAnswerRequestBody(
	hasAnswer: boolean,
	value: string | null,
	comment: string | null,
	selections: {option_id: number; comment: string | null}[],
): FeedbackAnswerUpdateRequest {
	return {
		state: hasAnswer ? FeedbackAnswerState.ANSWERED : FeedbackAnswerState.OMITTED,
		value,
		comment,
		selections,
	};
}

function serializeDateValue(value: unknown): string | null {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : formatDateValue(value);
	}

	const normalizedValue = normalizeTextValue(value);
	if (normalizedValue === null) {
		return null;
	}

	if (/^\d{4}-\d{2}-\d{2}/.test(normalizedValue)) {
		return normalizedValue.slice(0, 10);
	}

	const parsedDate = new Date(normalizedValue);
	return Number.isNaN(parsedDate.getTime()) ? null : formatDateValue(parsedDate);
}

function serializeTimeValue(value: unknown): string | null {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : formatTimeValue(value);
	}

	const normalizedValue = normalizeTextValue(value);
	if (normalizedValue === null) {
		return null;
	}

	const timeMatch = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/.exec(normalizedValue);
	if (timeMatch !== null) {
		const hours = Number(timeMatch[1]);
		const minutes = Number(timeMatch[2]);
		const seconds = Number(timeMatch[3] ?? '0');
		if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59) {
			return `${padDatePart(hours)}:${padDatePart(minutes)}:${padDatePart(seconds)}`;
		}
	}

	const parsedDate = new Date(normalizedValue);
	return Number.isNaN(parsedDate.getTime()) ? null : formatTimeValue(parsedDate);
}

function serializeDateTimeValue(value: unknown): string | null {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : formatDateTimeValue(value);
	}

	const normalizedValue = normalizeTextValue(value);
	if (normalizedValue === null) {
		return null;
	}

	if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:[+-]\d{2}:\d{2}|Z)$/.test(normalizedValue)) {
		return normalizedValue.endsWith('Z') ? normalizedValue.slice(0, -1) + '+00:00' : normalizedValue;
	}

	const parsedDate = new Date(normalizedValue);
	return Number.isNaN(parsedDate.getTime()) ? null : formatDateTimeValue(parsedDate);
}

function formatDateValue(date: Date): string {
	return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

function formatTimeValue(date: Date): string {
	return `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(date.getSeconds())}`;
}

function formatDateTimeValue(date: Date): string {
	return `${formatDateValue(date)}T${formatTimeValue(date)}${formatTimezoneOffset(date)}`;
}

function formatTimezoneOffset(date: Date): string {
	const offsetMinutes = -date.getTimezoneOffset();
	const sign = offsetMinutes >= 0 ? '+' : '-';
	const absoluteOffsetMinutes = Math.abs(offsetMinutes);
	const hours = Math.floor(absoluteOffsetMinutes / 60);
	const minutes = absoluteOffsetMinutes % 60;

	return `${sign}${padDatePart(hours)}:${padDatePart(minutes)}`;
}

function padDatePart(value: number): string {
	return String(value).padStart(2, '0');
}
